import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './components/ui/dialog';
import { Button } from './components/ui/button';
import { generatePDF } from './labelGenerator';
import { useToast } from './hooks/use-toast';
import { sendPrintJob } from './services/printService';

const SERVER_API_URL = import.meta.env.VITE_SERVER_API_URL || 'http://localhost:5000';

const BulkPrintModal = ({ 
  isOpen, 
  onClose, 
  selectedItems, 
  shipmentItems, 
  availablePrinters, 
  autoPrintEnabled, 
  clientInfo,
  quantitiesInBoxes = {}
}) => {
  const [quantities, setQuantities] = useState({});
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [importInfoMap, setImportInfoMap] = useState({});
  const [isLoadingImportInfo, setIsLoadingImportInfo] = useState(false);
  const { toast } = useToast();

  // Initialize quantities when modal opens or selected items change
  useEffect(() => {
    if (isOpen && selectedItems.size > 0) {
      const initialQuantities = {};
      selectedItems.forEach(itemId => {
        const shipmentItem = shipmentItems.find(item => item.id === itemId);
        if (shipmentItem) {
          // Calculate remaining quantity to be boxed
          const totalQuantity = shipmentItem.quantity || 0;
          const boxedQuantity = quantitiesInBoxes[shipmentItem.item.merchant_sku] || 0;
          const remainingQuantity = Math.max(totalQuantity - boxedQuantity, 1);
          initialQuantities[itemId] = remainingQuantity;
        }
      });
      setQuantities(initialQuantities);
      
      // Fetch import info for US shipments
      if (clientInfo && clientInfo.country === 'USA') {
        fetchImportInfo();
      }
    }
  }, [isOpen, selectedItems, shipmentItems, quantitiesInBoxes, clientInfo]);

  // Fetch import info (country of origin) for all selected items
  const fetchImportInfo = async () => {
    setIsLoadingImportInfo(true);
    const importData = {};
    
    try {
      const selectedItemsArray = Array.from(selectedItems);
      const fetchPromises = selectedItemsArray.map(async (itemId) => {
        const shipmentItem = shipmentItems.find(item => item.id === itemId);
        if (shipmentItem && shipmentItem.item?.asin) {
          try {
            const response = await fetch(`${SERVER_API_URL}/api/usa-import-info/${shipmentItem.item.asin}`);
            if (response.ok) {
              const data = await response.json();
              if (data) {
                importData[itemId] = {
                  country_of_origin: data.country_of_origin || '',
                  hts_code: data.hts_code || '',
                  origin_photos: data.origin_photos || []
                };
              }
            }
          } catch (error) {
            console.error(`Error fetching import info for ASIN ${shipmentItem.item.asin}:`, error);
          }
        }
      });
      
      await Promise.all(fetchPromises);
      setImportInfoMap(importData);
    } catch (error) {
      console.error('Error fetching import info:', error);
    } finally {
      setIsLoadingImportInfo(false);
    }
  };

  // Load saved printer preference
  useEffect(() => {
    if (availablePrinters.length > 0) {
      const savedPrinter = localStorage.getItem('selectedPrinter');
      if (savedPrinter && availablePrinters.some(p => p.id === savedPrinter)) {
        setSelectedPrinter(savedPrinter);
      } else {
        setSelectedPrinter(availablePrinters[0].id);
      }
    }
  }, [availablePrinters]);

  const handleQuantityChange = (itemId, value) => {
    const numValue = parseInt(value) || 0;
    setQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, numValue)
    }));
  };

  const showToast = (message, variant = 'default') => {
    toast({
      title: message,
      variant: variant
    });
  };

  // Helper function to check if expiration date is the forbidden placeholder
  const isForbiddenExpiryDate = (date) => {
    if (!date) return false;
    const checkDate = new Date(date);
    const forbiddenDate = new Date('2030-01-01');
    return checkDate.getFullYear() === forbiddenDate.getFullYear() && 
           checkDate.getMonth() === forbiddenDate.getMonth() && 
           checkDate.getDate() === forbiddenDate.getDate();
  };

  const handlePrint = async () => {
    if (selectedItems.size === 0) {
      showToast('No items selected', 'error');
      return;
    }

    // Validate quantities
    const validItems = Array.from(selectedItems).filter(itemId => 
      quantities[itemId] && quantities[itemId] > 0
    );

    if (validItems.length === 0) {
      showToast('Please enter valid quantities for at least one item', 'error');
      return;
    }

    // Check for US client and missing country of origin
    if (clientInfo && clientInfo.country === 'USA') {
      const itemsMissingCountryOfOrigin = [];
      for (const itemId of validItems) {
        const shipmentItem = shipmentItems.find(item => item.id === itemId);
        const importInfo = importInfoMap[itemId];
        if (shipmentItem && !importInfo?.country_of_origin) {
          itemsMissingCountryOfOrigin.push(shipmentItem.item.merchant_sku || shipmentItem.item.title);
        }
      }
      
      if (itemsMissingCountryOfOrigin.length > 0) {
        const itemList = itemsMissingCountryOfOrigin.length > 3 
          ? `${itemsMissingCountryOfOrigin.slice(0, 3).join(', ')} and ${itemsMissingCountryOfOrigin.length - 3} more`
          : itemsMissingCountryOfOrigin.join(', ');
        showToast(`Please select the Country of Origin for this US shipment before printing labels. Missing for: ${itemList}`, 'warning');
        return;
      }
    }

    // Check for forbidden expiration dates (Jan 1, 2030 placeholder)
    const itemsWithForbiddenDates = [];
    for (const itemId of validItems) {
      const shipmentItem = shipmentItems.find(item => item.id === itemId);
      if (shipmentItem && isForbiddenExpiryDate(shipmentItem.expiry_date)) {
        itemsWithForbiddenDates.push(shipmentItem.item.merchant_sku || shipmentItem.item.title);
      }
    }

    if (itemsWithForbiddenDates.length > 0) {
      const itemList = itemsWithForbiddenDates.length > 3 
        ? `${itemsWithForbiddenDates.slice(0, 3).join(', ')} and ${itemsWithForbiddenDates.length - 3} more`
        : itemsWithForbiddenDates.join(', ');
      showToast(`Cannot print labels with temporary expiration date (Jan 1, 2030). Please set proper expiration dates for: ${itemList}`, 'warning');
      return;
    }

    // Check auto print requirements
    if (autoPrintEnabled && (!selectedPrinter || availablePrinters.length === 0)) {
      showToast('Please select a printer for auto-print', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      // Save selected printer
      if (autoPrintEnabled && selectedPrinter) {
        localStorage.setItem('selectedPrinter', selectedPrinter);
      }

      // Create combined PDF
      await generateBulkPDF(validItems, autoPrintEnabled ? selectedPrinter : null, importInfoMap);

      showToast(`Successfully processed ${validItems.length} items for bulk printing`, 'success');
      onClose();
    } catch (error) {
      console.error('Error in bulk printing:', error);
      showToast(`Error in bulk printing: ${error.message}`, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Generate ONE combined PDF with separators and real FNSKU labels
  const generateBulkPDF = async (validItems, printer) => {
    const jsPDF = (await import('jspdf')).default;
    const JsBarcode = (await import('jsbarcode')).default;
    
    // Create ONE master PDF document
    const masterDoc = new jsPDF({
      orientation: 'landscape',
      unit: 'in',
      format: [2.25, 1.25],
    });

    let isFirstPage = true;
    
    // Create canvas for barcode generation
    const canvas = document.createElement('canvas');
    canvas.width = 360;
    canvas.height = 72;

    // Process each item with separators and real FNSKU labels
    for (let itemIndex = 0; itemIndex < validItems.length; itemIndex++) {
      const itemId = validItems[itemIndex];
      const shipmentItem = shipmentItems.find(item => item.id === itemId);
      const quantity = quantities[itemId];
      
      if (!shipmentItem || !quantity) continue;

      // Add separator before each item (including the first one)
      if (!isFirstPage) masterDoc.addPage();
      addSeparatorToMasterDoc(masterDoc, shipmentItem, quantity);
      isFirstPage = false;

      // Get import info for this shipment item
      const importInfo = importInfoMap[shipmentItem.id] || null;

      // Generate FNSKU labels for this item and add to master doc
      for (let labelNum = 0; labelNum < quantity; labelNum++) {
        if (!isFirstPage) masterDoc.addPage();
        await addFNSKULabelToMasterDoc(masterDoc, shipmentItem, labelNum + 1, quantity, canvas, JsBarcode, importInfo);
        isFirstPage = false;
      }
    }

    // Handle the final combined PDF
    if (printer) {
      // Auto print the combined PDF
      const pdfBlob = masterDoc.output('blob');
      const reader = new FileReader();
      reader.readAsDataURL(pdfBlob);

      return new Promise((resolve, reject) => {
        reader.onloadend = async () => {
          try {
            const base64Data = reader.result.split(',')[1];
            const fileName = `bulk_print_${validItems.length}_items.pdf`;

            const result = await sendPrintJob(printer, base64Data, fileName);
            console.log(`Combined bulk print job sent successfully to ${result.printer}`);
            resolve();
          } catch (error) {
            console.error('Error printing combined bulk labels:', error);
            reject(error);
          }
        };
      });
    } else {
      // Download the combined PDF
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      masterDoc.save(`bulk-labels-combined-${timestamp}.pdf`);
    }

    console.log(`Bulk print completed for ${validItems.length} items`);
  };





  // Add separator directly to master document
  const addSeparatorToMasterDoc = (masterDoc, shipmentItem, quantity) => {
    const pageWidth = masterDoc.internal.pageSize.getWidth();
    const pageHeight = masterDoc.internal.pageSize.getHeight();
    
    // Draw thick black border around entire label (edge to edge)
    masterDoc.setLineWidth(0.12); // Even thicker border
    masterDoc.setDrawColor(0, 0, 0);
    masterDoc.rect(0, 0, pageWidth, pageHeight);
    
    // Add bold "DO NOT USE AS LABEL" text at top
    masterDoc.setFontSize(12);
    masterDoc.setFont(undefined, 'bold');
    masterDoc.setTextColor(0, 0, 0);
    masterDoc.text('DO NOT USE AS LABEL', pageWidth / 2, 0.25, { align: 'center' });
    
    // Draw horizontal line below the warning (moved up to give more space for title)
    masterDoc.setLineWidth(0.03);
    masterDoc.setDrawColor(0, 0, 0);
    masterDoc.line(0.1, 0.32, pageWidth - 0.1, 0.32);
    
    // Add item title - much larger font and more space
    masterDoc.setFont(undefined, 'normal');
    masterDoc.setFontSize(10);
    const title = shipmentItem.item.title || 'Unknown Item';
    const maxWidth = pageWidth - 0.2;
    
    // Split text if it's too long - allow more lines since we removed SKU
    const titleLines = masterDoc.splitTextToSize(title, maxWidth);
    const linesToShow = titleLines.slice(0, 4);
    
    // Move title down more to avoid cutting off by the line
    const titleStartY = 0.50; // Increased from 0.45 to 0.50
    const lineHeight = 0.12;
    
    linesToShow.forEach((line, index) => {
      masterDoc.text(line, pageWidth / 2, titleStartY + (index * lineHeight), { align: 'center' });
    });

    // Add quantity information in bottom right corner
    masterDoc.setFont(undefined, 'bold');
    masterDoc.setFontSize(8);
    masterDoc.text(`${quantity} labels`, pageWidth - 0.1, pageHeight - 0.1, { align: 'right' });
    
    // IMPORTANT: Reset font properties so they don't carry over to next FNSKU label
    masterDoc.setFont(undefined, 'normal');
    masterDoc.setFontSize(12);
  };

  // Add real FNSKU label to master document (copied logic from labelGenerator.jsx)
  const addFNSKULabelToMasterDoc = async (masterDoc, item, labelNumber, totalQuantity, canvas, JsBarcode, importInfo = null) => {
    // Get FNSKU using same logic as labelGenerator.jsx
    let fnsku;
    try {
      const cacheKey = 'fnsku_cache';
      const cache = JSON.parse(localStorage.getItem(cacheKey) || '{}');
      const sku = item?.item?.merchant_sku;
      
      if (cache[sku]?.fnsku) {
        fnsku = cache[sku].fnsku;
      } else {
        fnsku = item?.item?.fnsku || 
                item?.fnsku || 
                item?.item?.identifiers?.find?.(id => id.identifier_type === 'FNSKU')?.identifier || 
                item?.item?.identifiers?.find?.(id => id.type === 'FNSKU')?.identifier;
      }

      if (!fnsku || fnsku === 'UNKNOWN') {
        throw new Error('No valid FNSKU found');
      }
    } catch (error) {
      console.error('Error retrieving FNSKU:', error);
      return;
    }

    // Generate barcode
    JsBarcode(canvas, fnsku, {
      format: "CODE128",
      displayValue: false,
      margin: 0,
    });

    const barcodeDataUrl = canvas.toDataURL('image/png');

    // Calculate layout (same logic as labelGenerator.jsx)
    const isUSShipment = clientInfo && clientInfo.country === 'USA';
    const expiryDate = item.expiry_date ? new Date(item.expiry_date) : null;
    const expiryDateText = expiryDate ? `, Exp: ${expiryDate.toISOString().split('T')[0]}` : '';
    const conditionText = `New${expiryDateText}`;
    
    let madeInYPosition, barcodeYPosition, fnskuYPosition, productNameStartY;
    
    if (isUSShipment) {
      madeInYPosition = 0.15;
      barcodeYPosition = 0.22;
      fnskuYPosition = 0.67;
      productNameStartY = 0.76;
    } else {
      barcodeYPosition = 0.15;
      fnskuYPosition = 0.6;
      productNameStartY = 0.69;
    }

    const pageWidth = masterDoc.internal.pageSize.getWidth();
    const leftMargin = 0.125;
    const rightMargin = 0.125;

    // Add "Made in XXX" for US shipments
    if (isUSShipment && importInfo && importInfo.country_of_origin) {
      const countryOfOrigin = importInfo.country_of_origin;
      let displayCountry = countryOfOrigin === 'United States' ? 'USA' : countryOfOrigin;
      masterDoc.setFontSize(8);
      masterDoc.setFont(undefined, 'normal');
      const madeInText = `Made in ${displayCountry}`;
      masterDoc.text(madeInText, pageWidth / 2, madeInYPosition, { align: 'center' });
    }

    // Add barcode
    masterDoc.addImage(barcodeDataUrl, 'PNG', 0.125, barcodeYPosition, 2, 0.28);

    // FNSKU on left, condition on right
    masterDoc.setFontSize(8);
    masterDoc.text(fnsku, leftMargin, fnskuYPosition, { align: 'left' });
    masterDoc.text(conditionText, pageWidth - rightMargin, fnskuYPosition, { align: 'right' });

    // Product name with complex text fitting (simplified version)
    masterDoc.setFontSize(8);
    const lineHeight = 0.1;
    const maxProductNameWidth = pageWidth - leftMargin - rightMargin - 0.05;
    const labelBottom = 1.15;
    const availableHeight = labelBottom - productNameStartY - 0.02;
    const maxLines = Math.floor(availableHeight / lineHeight);

    let productName = item.item.title;
    const words = productName.split(' ');
    const lines = [];
    let currentLine = '';

    // Simple word wrapping
    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      const word = words[wordIndex];
      const testLine = currentLine ? currentLine + ' ' + word : word;
      const testWidth = masterDoc.getTextWidth(testLine);
      
      if (testWidth <= maxProductNameWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = word;
        }
        
        if (lines.length >= maxLines) {
          break;
        }
      }
    }
    
    if (currentLine && lines.length < maxLines) {
      lines.push(currentLine);
    }

    // Print product name lines (center aligned)
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const yPosition = productNameStartY + (lineIndex * lineHeight);
      masterDoc.text(lines[lineIndex], pageWidth / 2, yPosition, { align: 'center', baseline: 'top' });
    }
  };

  const selectedItemsArray = Array.from(selectedItems).map(itemId => 
    shipmentItems.find(item => item.id === itemId)
  ).filter(Boolean);

  const totalLabels = Object.values(quantities).reduce((sum, qty) => sum + (qty || 0), 0);
  
  // Check for items with forbidden expiration dates for UI warnings
  const itemsWithForbiddenDatesCount = selectedItemsArray.filter(item => 
    isForbiddenExpiryDate(item.expiry_date)
  ).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!w-[70vw] !max-w-none h-[60vh] overflow-hidden mx-4">
        <DialogHeader>
          <DialogTitle>Bulk Print Labels ({selectedItems.size} items selected)</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col h-full max-h-[calc(60vh-8rem)]">
          {/* Table Header */}
          <div className="bg-gray-50 border border-gray-200 rounded-t-lg px-4 py-2 flex-shrink-0">
            <div className="grid grid-cols-10 gap-2 text-xs font-medium text-gray-700 uppercase">
              <div className="col-span-1">Image</div>
              <div className="col-span-5">Product Details</div>
              <div className="col-span-2">Qty (Total/Boxed/Remaining)</div>
              <div className="col-span-2">Labels to Print</div>
            </div>
          </div>
          
          {/* Items List */}
          <div className="flex-1 overflow-y-auto border-l border-r border-b border-gray-200 rounded-b-lg min-h-0 max-h-[30vh]">
            {selectedItemsArray.map((item) => {
              if (!item) return null;
              
              const itemQuantity = quantities[item.id] || 0;
              const totalQuantity = item.quantity || 0;
              const boxedQuantity = quantitiesInBoxes[item.item.merchant_sku] || 0;
              const remainingQuantity = totalQuantity - boxedQuantity;
              const hasForbiddenDate = isForbiddenExpiryDate(item.expiry_date);
              
              return (
                <div key={item.id} className={`border-b border-gray-100 hover:bg-gray-50 px-4 py-2 ${hasForbiddenDate ? 'bg-red-50 border-red-200' : ''}`}>
                  <div className="grid grid-cols-10 gap-2 items-center">
                    {/* Product Image */}
                    <div className="col-span-1">
                      <img
                        src={item.item.images?.[0]?.large_url || 'https://via.placeholder.com/32?text=No+Img'}
                        alt={item.item.title}
                        className="w-8 h-8 object-cover rounded border"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/32?text=No+Img';
                        }}
                      />
                    </div>
                    
                    {/* Product Details */}
                    <div className="col-span-5">
                      <div className="text-xs font-medium text-gray-900 truncate" title={item.item.title}>
                        {item.item.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                        <div>SKU: <span className="font-mono">{item.item.merchant_sku}</span></div>
                        <div>ASIN: <span className="font-mono">{item.item.asin || 'N/A'}</span></div>
                        <div>FNSKU: <span className="font-mono">{item.item.fnsku || 'N/A'}</span></div>
                        {hasForbiddenDate && (
                          <div className="flex items-center text-red-600 font-medium">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Temp. expiry date (Jan 1, 2030)
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Quantities */}
                    <div className="col-span-2">
                      <div className="text-xs text-gray-600 font-mono">
                        {totalQuantity} / {boxedQuantity} / {remainingQuantity}
                      </div>
                    </div>
                    
                    {/* Label Input */}
                    <div className="col-span-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0"
                          value={itemQuantity}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          className="w-12 px-1 py-0.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleQuantityChange(item.id, remainingQuantity)}
                          className="text-xs px-1 py-0.5 h-6"
                        >
                          Max
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Panel */}
          <div className="mt-2 space-y-1 flex-shrink-0">
            {/* Print Configuration */}
            {autoPrintEnabled && (
              <div className="p-1.5 bg-gray-50 border border-gray-200 rounded">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-700">Printer:</span>
                  <select
                    value={selectedPrinter}
                    onChange={(e) => setSelectedPrinter(e.target.value)}
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {availablePrinters.map((printer) => (
                      <option key={printer.id} value={printer.id}>
                        {printer.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="p-1.5 bg-blue-50 border border-blue-200 rounded">
              <div className="text-xs text-blue-800">
                <strong>Print Summary:</strong> {totalLabels} total labels for {selectedItemsArray.length} items
                {selectedItemsArray.length > 1 && (
                  <span className="text-xs text-blue-600">
                    + Separator labels between items
                  </span>
                )}
              </div>
            </div>

            {/* Warning for forbidden expiration dates */}
            {itemsWithForbiddenDatesCount > 0 && (
              <div className="p-1.5 bg-red-50 border border-red-200 rounded">
                <div className="text-xs text-red-800 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <strong>Warning:</strong> {itemsWithForbiddenDatesCount} item{itemsWithForbiddenDatesCount > 1 ? 's have' : ' has'} temporary expiration date (Jan 1, 2030). Please update before printing.
                </div>
              </div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-between mt-4 pt-4 border-t border-gray-200 flex-shrink-0">
            <Button variant="outline" onClick={onClose} disabled={isProcessing}>
              Cancel
            </Button>
            <Button 
              onClick={handlePrint} 
              disabled={isProcessing || totalLabels === 0 || itemsWithForbiddenDatesCount > 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? (
                <>Processing...</>
              ) : itemsWithForbiddenDatesCount > 0 ? (
                <>Cannot Print - Fix Expiry Dates</>
              ) : (
                <>Print {totalLabels} Labels {autoPrintEnabled ? '(Auto-Print)' : '(Download PDF)'}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPrintModal; 